import React from "react";
import { Box, Container, Typography, Button, Paper, useTheme, useMediaQuery } from "@mui/material";
import { Home as HomeIcon, Search as SearchIcon, ErrorOutline as ErrorIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function NotFound() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const navigate = useNavigate();

	const handleGoHome = () => {
		navigate("/");
	};

	const handleGoBack = () => {
		navigate(-1);
	};

	return (
		<Container maxWidth="md" sx={{ py: 8 }}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "70vh",
					textAlign: "center",
				}}
			>
				{/* 404 Icon */}
				<Box
					sx={{
						position: "relative",
						mb: 4,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<ErrorIcon
						sx={{
							fontSize: isMobile ? "4rem" : "6rem",
							color: theme.palette.primary.main,
							opacity: 0.1,
							position: "absolute",
						}}
					/>
					<Typography
						variant={isMobile ? "h1" : "display1"}
						component="h1"
						sx={{
							fontWeight: 700,
							color: theme.palette.primary.main,
							fontSize: isMobile ? "4rem" : "6rem",
							lineHeight: 1,
							textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
						}}
					>
						404
					</Typography>
				</Box>

				{/* Main Message */}
				<Paper
					elevation={2}
					sx={{
						p: isMobile ? 3 : 4,
						borderRadius: 3,
						backgroundColor: "rgba(255, 255, 255, 0.9)",
						backdropFilter: "blur(10px)",
						border: `1px solid ${theme.palette.primary.light}`,
						maxWidth: "500px",
						width: "100%",
					}}
				>
					<Typography
						variant={isMobile ? "h4" : "h3"}
						component="h2"
						gutterBottom
						sx={{
							fontWeight: 600,
							color: theme.palette.text.primary,
							mb: 2,
						}}
					>
						Page Not Found
					</Typography>

					<Typography
						variant="body1"
						color="text.secondary"
						sx={{
							mb: 4,
							fontSize: isMobile ? "1rem" : "1.1rem",
							lineHeight: 1.6,
						}}
					>
						The page you're looking for doesn't exist or has been moved. Please check the URL or use the navigation below.
					</Typography>

					{/* Action Buttons */}
					<Box
						sx={{
							display: "flex",
							flexDirection: isMobile ? "column" : "row",
							gap: 2,
							justifyContent: "center",
							alignItems: "stretch",
						}}
					>
						<Button
							variant="contained"
							size="large"
							startIcon={<HomeIcon />}
							onClick={handleGoHome}
							sx={{
								py: 1.5,
								px: 3,
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
								boxShadow: "0 4px 12px rgba(0, 84, 166, 0.3)",
								"&:hover": {
									boxShadow: "0 6px 20px rgba(0, 84, 166, 0.4)",
									transform: "translateY(-2px)",
								},
								transition: "all 0.3s ease",
							}}
						>
							Go Home
						</Button>

						<Button
							variant="outlined"
							size="large"
							startIcon={<SearchIcon />}
							onClick={handleGoBack}
							sx={{
								py: 1.5,
								px: 3,
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
								borderColor: theme.palette.primary.main,
								color: theme.palette.primary.main,
								"&:hover": {
									borderColor: theme.palette.primary.dark,
									backgroundColor: "rgba(0, 84, 166, 0.04)",
									transform: "translateY(-2px)",
								},
								transition: "all 0.3s ease",
							}}
						>
							Go Back
						</Button>
					</Box>
				</Paper>

				{/* Additional Help Text */}
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{
						mt: 4,
						opacity: 0.7,
						maxWidth: "400px",
					}}
				>
					If you believe this is an error, please contact our support team.
				</Typography>
			</Box>
		</Container>
	);
}
